import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { compose } from 'recompose';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { observer } from 'mobx-react';
import { useRouteMatch } from 'react-router-dom';
import format from 'string-template';
import Switch from '@mui/material/Switch';
import Collapse from '@mui/material/Collapse';

import Product from '../../../../../Model/Product';
import Program from '../../../../../Model/Program';
import MealPlanView from '../../../../Model/MealPlanView';
import ChallengeConfig from '../../../../Model/ChallengeConfig';
import UserContract from '../../../../../Model/UserContract';
import useStorage, { StorageProcessState } from '../../../../../hooks/useStorage';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useSessionStore from '../../../../../hooks/useSessionStore';
import { storagePaths, pathPlaceholder } from '../../../../../utils/firebasePaths';
import { DEFAULT_CURRENCY } from '../../../../../utils/currency';
import SaveButton from '../../../../../components/Button/ActionButtons/SaveButton';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import ConfirmDialog from '../../../../components/ConfirmDialog';
import useToast from '../../../../hooks/useToast';
import UpsellConfig, {
  UpsellTouchpointKey,
  UpsellOfferType,
  UpsellMediaType,
  createEmptyStrategy,
  createEmptyExperiment,
} from '../../../../Model/UpsellConfig';
import { ContractStatus } from '../../../../utils/userContract';
import { useChallengeUpsellPreEnrollEnabled, useUpsellPopupExperimentEnabled } from '../../../../../utils/postHog';
import ExternalCoachContext from '../../../../context/ExternalCoachContext';
import {
  withExternalCoachContextReady,
} from '../../../../context/ExternalCoachContext/withExternalCoachContext';
import { Title, Description } from '../../styles';
import commonTexts from '../texts.json';
import screenTexts from './texts.json';
import {
  ContentWrapper,
  SectionContainer,
  SectionTitle,
  SectionDescription,
  Row,
  StyledFormControlLabel,
  SearchField,
  CollapseHeader,
  Chevron,
  SectionActions,
} from './styles';
import StrategyEditor from './StrategyEditor';
import ChallengeList from './ChallengeList';
import {
  fromDraftOffer,
  toDraftStrategy,
  snapshotStrategy,
  findInvalidDraftTouchpoint,
  toStoredCheckoutPreEnroll,
  isDraftCheckoutPreEnrollValid,
  isCoachingProduct,
  isChallengeOfferProduct,
  offerCurrencyForContract,
} from './utils';

const texts = { ...commonTexts, ...screenTexts };

const touchpointTitles = {
  [UpsellTouchpointKey.SEVEN_DAYS_BEFORE]: texts.touchpointSevenDays,
  [UpsellTouchpointKey.THREE_DAYS_BEFORE]: texts.touchpointThreeDays,
  [UpsellTouchpointKey.LAST_DAY]: texts.touchpointLastDay,
};

/** The strategy with a patch merged into its pop-up A/B test, which is created when the first patch switches it on. */
const patchExperiment = (strategy, patch) => ({
  ...strategy,
  experiment: { ...(strategy.experiment || createEmptyExperiment()), ...patch },
});

/** The strategy with a patch merged into one of its pop-up A/B test's group B touchpoints. */
const patchExperimentTouchpoint = (strategy, touchpointKey, patch) => patchExperiment(strategy, {
  touchpoints: {
    ...strategy.experiment.touchpoints,
    [touchpointKey]: { ...strategy.experiment.touchpoints[touchpointKey], ...patch },
  },
});

const UpsellSettings = () => {
  const { params: { userId: coachId } } = useRouteMatch();
  const { coachDoc } = useContext(ExternalCoachContext);
  const { isAdmin, authUser } = useSessionStore();
  const { uploadAttachment } = useStorage();
  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();
  const isCheckoutPreEnrollEnabled = useChallengeUpsellPreEnrollEnabled(coachId);
  const isPopupExperimentEnabled = useUpsellPopupExperimentEnabled(coachId);

  const [isReady, setIsReady] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [upsellConfigDoc, setUpsellConfigDoc] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [defaultStrategy, setDefaultStrategy] = useState(createEmptyStrategy());
  const [challenges, setChallenges] = useState({});
  const [challengeContracts, setChallengeContracts] = useState([]);
  const [products, setProducts] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [mealPlans, setMealPlans] = useState([]);
  const [challengeSearch, setChallengeSearch] = useState('');
  const [isDefaultExpanded, setIsDefaultExpanded] = useState(false);
  const [pendingEnabledChange, setPendingEnabledChange] = useState(null);
  const [expandedChallengeId, setExpandedChallengeId] = useState('');

  useEffect(() => {
    let isStaleRun = false;
    const init = async () => {
      /*
        Products come from a single flat query here instead of the context's
        withCoachProductsContextReady gate — that gate also loads every
        product's plans serially, which this section doesn't need.
      */
      const [
        configDoc,
        contractsCollection,
        productsCollection,
        coachPrograms,
        publicPrograms,
        mealPlanViews,
      ] = await Promise.all([
        UpsellConfig.getByCoachId(coachId),
        UserContract.getBaseContractsByCoach(coachId),
        Product.getProductByCoachId(coachId),
        Program.getProgramsByCoach(coachId),
        Program.getPublicPrograms(),
        MealPlanView.getMealPlanViewsByCoach(coachId),
      ]);
      const contracts = contractsCollection.docs.filter((contract) => (
        !!contract.challengeId && contract.status !== ContractStatus.CANCELED
      ));

      if (isStaleRun || !isComponentMountedRef.current) {
        return;
      }
      setProducts(productsCollection.docs.filter((product) => !!product.name));
      setPrograms([...coachPrograms.docs, ...publicPrograms.docs]);
      setMealPlans(mealPlanViews.docs.filter((mealPlan) => !mealPlan.isArchived));
      setUpsellConfigDoc(configDoc);
      setEnabled(configDoc.enabled);
      setDefaultStrategy(toDraftStrategy(configDoc.defaultStrategy));
      setChallenges(Object.fromEntries(Object.entries(configDoc.challenges).map(([contractId, entry]) => [
        contractId,
        { useDefault: entry.useDefault !== false, custom: entry.custom ? toDraftStrategy(entry.custom) : null },
      ])));
      setChallengeContracts(contracts);
      setIsReady(true);
    };
    // Re-runs on coachId change — gating on isReady left this saving to the previous coach.
    setIsReady(false);
    setHasLoadError(false);
    init().catch(() => {
      if (isStaleRun || !isComponentMountedRef.current) {
        return;
      }
      setHasLoadError(true);
      showToast(texts.loadError, { error: true });
    });
    return () => {
      isStaleRun = true;
    };
  }, [coachId, isComponentMountedRef, showToast]);

  /*
    Offers are priced in the coach's own contract currency — an offer in another
    currency is rejected at accept time by the server. A coach whose challenge
    clients span two currencies cannot be served by one price at all; that needs
    per-currency pricing on the offer rather than a better guess here.
  */
  const coachCurrency = (challengeContracts.find((contract) => !!contract.currency)?.currency
    || DEFAULT_CURRENCY.code).toUpperCase();

  const normalizedSearch = challengeSearch.trim().toLowerCase();
  const filteredChallengeContracts = normalizedSearch
    ? challengeContracts.filter((contract) => (
      contract.id.toLowerCase().includes(normalizedSearch)
      || (contract.name || '').toLowerCase().includes(normalizedSearch)
      || (contract.productName || '').toLowerCase().includes(normalizedSearch)
    ))
    : challengeContracts;

  /*
    The master switch writes on change rather than waiting for a save button in
    the (collapsible) default strategy section — but it changes what every one
    of this coach's challenge clients sees, live, so it is confirmed first.
  */
  const handleConfirmEnabledChange = useCallback(async () => {
    const isEnabled = pendingEnabledChange;
    setPendingEnabledChange(null);
    setEnabled(isEnabled);
    try {
      await upsellConfigDoc.setEnabled(isEnabled, authUser?.displayName || '');
      showToast(isEnabled ? texts.upsellEnabled : texts.upsellDisabled);
    } catch (error) {
      if (isComponentMountedRef.current) {
        setEnabled(!isEnabled);
      }
      showToast(error.message || texts.uploadError, { error: true });
    }
  }, [pendingEnabledChange, upsellConfigDoc, authUser, showToast, isComponentMountedRef]);

  const handleDefaultTouchpointChange = useCallback((touchpointKey, patch) => {
    setDefaultStrategy((prev) => ({
      ...prev,
      touchpoints: {
        ...prev.touchpoints,
        [touchpointKey]: { ...prev.touchpoints[touchpointKey], ...patch },
      },
    }));
  }, []);

  const handleDefaultCheckoutPreEnrollChange = useCallback((patch) => {
    setDefaultStrategy((prev) => ({ ...prev, checkoutPreEnroll: { ...prev.checkoutPreEnroll, ...patch } }));
  }, []);

  const handleDefaultExperimentChange = useCallback((patch) => {
    setDefaultStrategy((prev) => patchExperiment(prev, patch));
  }, []);

  const handleDefaultExperimentTouchpointChange = useCallback((touchpointKey, patch) => {
    setDefaultStrategy((prev) => patchExperimentTouchpoint(prev, touchpointKey, patch));
  }, []);

  const handleToggleUseDefault = useCallback((contractId) => {
    setChallenges((prev) => {
      const entry = prev[contractId];
      const isCustom = !!entry && !entry.useDefault && !!entry.custom;
      if (isCustom) {
        // Back to default — keep the custom snapshot so the switch round-trips.
        return { ...prev, [contractId]: { ...entry, useDefault: true } };
      }
      return {
        ...prev,
        [contractId]: {
          useDefault: false,
          // Custom starts from a snapshot of the current default; a kept
          // snapshot from a previous switch is reused.
          custom: entry?.custom || snapshotStrategy(defaultStrategy),
        },
      };
    });
  }, [defaultStrategy]);

  const handleCustomTouchpointChange = useCallback((contractId, touchpointKey, patch) => {
    setChallenges((prev) => {
      const entry = prev[contractId];
      return {
        ...prev,
        [contractId]: {
          ...entry,
          custom: {
            ...entry.custom,
            touchpoints: {
              ...entry.custom.touchpoints,
              [touchpointKey]: { ...entry.custom.touchpoints[touchpointKey], ...patch },
            },
          },
        },
      };
    });
  }, []);

  const handleCustomCheckoutPreEnrollChange = useCallback((contractId, patch) => {
    setChallenges((prev) => {
      const entry = prev[contractId];
      return {
        ...prev,
        [contractId]: {
          ...entry,
          custom: { ...entry.custom, checkoutPreEnroll: { ...entry.custom.checkoutPreEnroll, ...patch } },
        },
      };
    });
  }, []);

  const handleCustomExperimentChange = useCallback((contractId, patch) => {
    setChallenges((prev) => ({
      ...prev,
      [contractId]: { ...prev[contractId], custom: patchExperiment(prev[contractId].custom, patch) },
    }));
  }, []);

  const handleCustomExperimentTouchpointChange = useCallback((contractId, touchpointKey, patch) => {
    setChallenges((prev) => ({
      ...prev,
      [contractId]: {
        ...prev[contractId],
        custom: patchExperimentTouchpoint(prev[contractId].custom, touchpointKey, patch),
      },
    }));
  }, []);

  const validateStrategy = useCallback((strategy, strategyName) => {
    const invalidTouchpoint = findInvalidDraftTouchpoint(strategy, { isTestGroupShown: isPopupExperimentEnabled });
    if (!invalidTouchpoint) {
      return isDraftCheckoutPreEnrollValid(strategy.checkoutPreEnroll)
        ? null
        : format(texts.validationCheckoutPreEnroll, { strategy: strategyName });
    }
    return format(invalidTouchpoint.isTestGroup ? texts.validationTestTouchpoint : texts.validationEnabledTouchpoint, {
      touchpoint: touchpointTitles[invalidTouchpoint.touchpointKey],
      strategy: strategyName,
    });
  }, [isPopupExperimentEnabled]);

  /**
   * The community tag of the coach's existing challenge for this product, so an
   * upsell client joins the same tag as one who bought the challenge normally.
   * That tag is named after the contract name a CAM typed, so it can only be
   * inherited — deriving one from the product would build a second community
   * alongside it. No base contract for the product yet means no tag to inherit.
   */
  const resolveChallengeTag = useCallback(async (productId) => {
    const baseContract = challengeContracts
      .filter((contract) => contract.productId === productId)
      .sort((a, b) => (b.createdAt?.toMillis() ?? 0) - (a.createdAt?.toMillis() ?? 0))[0];
    if (!baseContract?.challengeId) {
      return '';
    }
    const challengeConfigDoc = await ChallengeConfig.getChallengeConfigById(baseContract.challengeId);
    return challengeConfigDoc?.tag || '';
  }, [challengeContracts]);

  /**
   * Uploads any newly selected media of a strategy's three touchpoints (its own, or its pop-up A/B test's group B),
   * then returns them in their stored shape. Only `isActive` touchpoints — enabled, and for group B with the test on —
   * upload or mint anything: those are the ones validateStrategy checked.
   */
  const resolveTouchpointsForSave = useCallback(async (draftTouchpoints, currency, isActive) => {
    const storagePath = format(storagePaths.COACH_UPSELL_MEDIA, { [pathPlaceholder.COACH_ID]: coachId });
    const touchpoints = {};
    // Sequential on purpose: at most three small uploads per strategy.
    // eslint-disable-next-line no-restricted-syntax
    for (const touchpointKey of Object.values(UpsellTouchpointKey)) {
      const touchpoint = draftTouchpoints[touchpointKey];
      const isTouchpointActive = isActive && !!touchpoint.enabled;
      /*
        Only an enabled touchpoint uploads media or mints a challengeConfig. validateStrategy
        skips disabled ones, so acting on them here would write unvalidated configs (and
        files) that nothing reads unless the touchpoint is ever switched on. Its fields are
        still stored as drafted, so nothing is lost by disabling.
      */
      let { message } = touchpoint;
      if (isTouchpointActive && touchpoint.pendingAttachment) {
        const attachment = touchpoint.pendingAttachment;
        // eslint-disable-next-line no-await-in-loop
        const result = await uploadAttachment(
          attachment.dataUrl,
          attachment.format,
          storagePath,
          attachment.fileName,
          attachment.fileType,
        );
        if (result.state !== StorageProcessState.SUCCESS) {
          throw new Error(texts.uploadError);
        }
        message = {
          text: message.text || '',
          mediaType: attachment.fileType.startsWith('video/') ? UpsellMediaType.VIDEO : UpsellMediaType.IMAGE,
          mediaUrl: result.publicUrl,
          mediaPath: result.fileRef,
        };
      }
      let { offer } = touchpoint;
      if (isTouchpointActive && offer?.type === UpsellOfferType.CHALLENGE && offer.programId) {
        /*
          The offered challenge needs its own challengeConfig — that is what
          assigns the program and meal plan when the client's new challenge
          starts. Mirrors what the contract generator does when a challenge
          contract is saved.
        */
        const challengeConfigData = { programId: offer.programId, mealPlanId: offer.mealPlanId || '' };
        // eslint-disable-next-line no-await-in-loop
        const challengeTag = await resolveChallengeTag(offer.productId);
        if (challengeTag) {
          // Omitted rather than blanked when there is nothing to inherit, so a
          // re-save never clears a tag the challenge already had.
          challengeConfigData.tag = challengeTag;
        }
        let existingChallengeConfigDoc = null;
        if (offer.challengeConfigId) {
          // eslint-disable-next-line no-await-in-loop
          existingChallengeConfigDoc = await ChallengeConfig.getChallengeConfigById(offer.challengeConfigId);
        }
        if (existingChallengeConfigDoc) {
          // eslint-disable-next-line no-await-in-loop
          await existingChallengeConfigDoc.updateFields(challengeConfigData);
        } else {
          // eslint-disable-next-line no-await-in-loop
          const challengeConfigDoc = await ChallengeConfig.addDoc(challengeConfigData);
          offer = { ...offer, challengeConfigId: challengeConfigDoc.id };
        }
      }
      touchpoints[touchpointKey] = {
        enabled: !!touchpoint.enabled,
        message: {
          text: message.text || '',
          mediaType: message.mediaType || null,
          mediaUrl: message.mediaUrl || '',
          mediaPath: message.mediaPath || '',
        },
        offer: fromDraftOffer(offer && { ...offer, currency }),
      };
    }
    return touchpoints;
  }, [coachId, uploadAttachment, resolveChallengeTag]);

  /**
   * The strategy in its stored shape, newly selected media uploaded. `currency` is stamped onto every offer rather
   * than read back from the draft: it is derived from the contract being priced, so a config saved under an earlier
   * rule corrects itself here instead of being stuck at a value the form can no longer edit. The pop-up A/B test is
   * stored only once one was set up, so a strategy without one saves exactly as before.
   */
  const resolveStrategyForSave = useCallback(async (strategy, currency) => {
    const resolved = {
      touchpoints: await resolveTouchpointsForSave(strategy.touchpoints, currency, true),
      checkoutPreEnroll: toStoredCheckoutPreEnroll(strategy.checkoutPreEnroll, currency),
    };
    if (strategy.experiment) {
      resolved.experiment = {
        enabled: !!strategy.experiment.enabled,
        touchpoints: await resolveTouchpointsForSave(
          strategy.experiment.touchpoints,
          currency,
          !!strategy.experiment.enabled,
        ),
      };
    }
    return resolved;
  }, [resolveTouchpointsForSave]);

  /** Uploads pending media, then persists one strategy. Returns false when validation fails. */
  const saveStrategy = useCallback(async ({
    strategy, strategyName, persist, successText, currency,
  }) => {
    const validationError = validateStrategy(strategy, strategyName);
    if (validationError) {
      showToast(validationError, { error: true });
      return false;
    }

    setIsSaving(true);
    try {
      const resolvedStrategy = await resolveStrategyForSave(strategy, currency);
      await persist(resolvedStrategy);
      showToast(successText);
      return resolvedStrategy;
    } catch (error) {
      showToast(error.message || texts.uploadError, { error: true });
      return false;
    } finally {
      if (isComponentMountedRef.current) {
        setIsSaving(false);
      }
    }
  }, [validateStrategy, resolveStrategyForSave, showToast, isComponentMountedRef]);

  const handleSaveDefaultStrategy = useCallback(async () => {
    const resolvedStrategy = await saveStrategy({
      strategy: defaultStrategy,
      strategyName: texts.defaultStrategyName,
      successText: texts.savedDefault,
      currency: coachCurrency,
      persist: (resolved) => upsellConfigDoc.saveDefaultStrategy({
        defaultStrategy: resolved,
        lastUpdatedByUserName: authUser?.displayName || '',
      }),
    });
    if (resolvedStrategy && isComponentMountedRef.current) {
      setDefaultStrategy(toDraftStrategy(resolvedStrategy));
    }
  }, [saveStrategy, defaultStrategy, coachCurrency, upsellConfigDoc, authUser, isComponentMountedRef]);

  const handleSaveChallengeOverride = useCallback(async (contractId) => {
    const entry = challenges[contractId] || { useDefault: true, custom: null };
    const contract = challengeContracts.find(({ id }) => id === contractId);
    const strategyName = contract?.name || contract?.productName || contractId;
    const contractCurrency = offerCurrencyForContract(contract, coachCurrency);

    // A challenge back on the default has no strategy to validate or upload.
    if (entry.useDefault || !entry.custom) {
      setIsSaving(true);
      try {
        const resolvedSnapshot = entry.custom
          ? await resolveStrategyForSave(entry.custom, contractCurrency)
          : null;
        await upsellConfigDoc.saveChallengeOverride(
          contractId,
          { useDefault: true, custom: resolvedSnapshot },
          authUser?.displayName || '',
        );

        if (resolvedSnapshot && isComponentMountedRef.current) {
          setChallenges((prev) => ({
            ...prev,
            [contractId]: { useDefault: true, custom: toDraftStrategy(resolvedSnapshot) },
          }));
        }
        showToast(texts.savedChallenge);
      } catch (error) {
        showToast(error.message || texts.uploadError, { error: true });
      } finally {
        if (isComponentMountedRef.current) {
          setIsSaving(false);
        }
      }
      return;
    }

    const resolvedStrategy = await saveStrategy({
      strategy: entry.custom,
      strategyName,
      successText: texts.savedChallenge,
      currency: contractCurrency,
      persist: (resolved) => upsellConfigDoc.saveChallengeOverride(
        contractId,
        { useDefault: false, custom: resolved },
        authUser?.displayName || '',
      ),
    });
    if (resolvedStrategy && isComponentMountedRef.current) {
      setChallenges((prev) => ({
        ...prev,
        [contractId]: { useDefault: false, custom: toDraftStrategy(resolvedStrategy) },
      }));
    }
  }, [
    saveStrategy,
    challenges,
    challengeContracts,
    coachCurrency,
    upsellConfigDoc,
    resolveStrategyForSave,
    authUser,
    showToast,
    isComponentMountedRef,
  ]);

  const handleToggleChallengeExpanded = useCallback((contractId) => {
    setExpandedChallengeId((prev) => (prev === contractId ? '' : contractId));
  }, []);

  if (!isAdmin) {
    return (
      <ContentWrapper>
        <Title>{texts.title}</Title>
        <Description>{texts.adminOnly}</Description>
      </ContentWrapper>
    );
  }

  if (hasLoadError) {
    return (
      <ContentWrapper>
        <Title>{texts.title}</Title>
        <Description>{texts.loadError}</Description>
      </ContentWrapper>
    );
  }

  const activeProducts = products.filter((product) => !product.isArchived);
  const hasChallengeProduct = activeProducts.some(isChallengeOfferProduct);
  const hasCoachingProduct = activeProducts.some(isCoachingProduct);
  const isEligible = hasChallengeProduct && hasCoachingProduct && !coachDoc.isSelfServeCoach;

  // Products load asynchronously — only judge eligibility once they're in.
  if (isReady && !isEligible) {
    return (
      <ContentWrapper>
        <Title>{texts.title}</Title>
        <Description>
          {coachDoc.isSelfServeCoach ? texts.notEligibleLaunchpad : texts.notEligible}
        </Description>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper>
      <Title>{texts.title}</Title>
      <Description>{texts.description}</Description>
      <SectionContainer>
        <Row>
          <StyledFormControlLabel
            control={(
              <Switch
                checked={enabled}
                onChange={(event) => setPendingEnabledChange(event.target.checked)}
                color="primary"
                disabled={!isReady || isSaving}
              />
            )}
            label={texts.masterToggle}
          />
        </Row>
      </SectionContainer>
      <SectionContainer>
        <CollapseHeader onClick={() => setIsDefaultExpanded((prev) => !prev)}>
          <div>
            <SectionTitle>{texts.defaultStrategyTitle}</SectionTitle>
            <SectionDescription>{texts.defaultStrategyDescription}</SectionDescription>
          </div>
          <Chevron $isExpanded={isDefaultExpanded}><ExpandMoreIcon fontSize="small" /></Chevron>
        </CollapseHeader>
        <Collapse in={isDefaultExpanded} unmountOnExit>
          <div>
            <StrategyEditor
              strategy={defaultStrategy}
              onTouchpointChange={handleDefaultTouchpointChange}
              onCheckoutPreEnrollChange={handleDefaultCheckoutPreEnrollChange}
              showCheckoutPreEnroll={isCheckoutPreEnrollEnabled}
              onExperimentChange={handleDefaultExperimentChange}
              onExperimentTouchpointChange={handleDefaultExperimentTouchpointChange}
              showExperiment={isPopupExperimentEnabled}
              products={products}
              currency={coachCurrency}
              programs={programs}
              mealPlans={mealPlans}
            />
            <SectionActions>
              <SaveButton onClick={handleSaveDefaultStrategy} disabled={!isReady || isSaving} $fitToContent>
                {texts.saveButton}
              </SaveButton>
            </SectionActions>
          </div>
        </Collapse>
      </SectionContainer>
      <SectionContainer>
        <SectionTitle>{texts.challengesTitle}</SectionTitle>
        <SectionDescription>{texts.challengesDescription}</SectionDescription>
        <SearchField
          placeholder={texts.searchChallengesPlaceholder}
          value={challengeSearch}
          onChange={(event) => setChallengeSearch(event.target.value)}
          size="small"
        />
        <ChallengeList
          challengeContracts={filteredChallengeContracts}
          challenges={challenges}
          expandedChallengeId={expandedChallengeId}
          onToggleExpanded={handleToggleChallengeExpanded}
          onToggleUseDefault={handleToggleUseDefault}
          onCustomTouchpointChange={handleCustomTouchpointChange}
          onCustomCheckoutPreEnrollChange={handleCustomCheckoutPreEnrollChange}
          showCheckoutPreEnroll={isCheckoutPreEnrollEnabled}
          onCustomExperimentChange={handleCustomExperimentChange}
          onCustomExperimentTouchpointChange={handleCustomExperimentTouchpointChange}
          showExperiment={isPopupExperimentEnabled}
          isDefaultExperimentEnabled={!!defaultStrategy.experiment?.enabled}
          onSaveOverride={handleSaveChallengeOverride}
          isSaving={isSaving}
          products={products}
          currency={coachCurrency}
          programs={programs}
          mealPlans={mealPlans}
          emptyText={normalizedSearch ? texts.noChallengesFound : texts.noChallenges}
        />
      </SectionContainer>
      <ConfirmDialog
        isOpen={pendingEnabledChange !== null}
        onConfirm={handleConfirmEnabledChange}
        onCancel={() => setPendingEnabledChange(null)}
        dialogTexts={{
          title: pendingEnabledChange ? texts.confirmEnableTitle : texts.confirmDisableTitle,
          content: pendingEnabledChange ? texts.confirmEnableContent : texts.confirmDisableContent,
        }}
      />
      <LoadingOverlay isLoading={!isReady || isSaving} />
    </ContentWrapper>
  );
};

export default compose(
  withExternalCoachContextReady,
  observer,
)(UpsellSettings);
